import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { Modal, FormGroup, Input, Select, Textarea, Badge, Table, PageHeader, EmptyState, Loading, StatCard } from '../components/UI';
import api from '../services/api';

// ── API helpers ──────────────────────────────────────────────────────
const adminApi = {
  dashboard: () => api.get('/superadmin/dashboard'),
  tenants: (params) => api.get('/superadmin/tenants', { params }),
  getTenant: (id) => api.get(`/superadmin/tenants/${id}`),
  updateTenant: (id, data) => api.put(`/superadmin/tenants/${id}`, data),
  toggleTenant: (id) => api.post(`/superadmin/tenants/${id}/toggle`),
  extendPlan: (id, days) => api.post(`/superadmin/tenants/${id}/extend`, null, { params: { days } }),
  plans: () => api.get('/superadmin/plans'),
};

const PLAN_COLORS = { basic: 'secondary', professional: 'primary', enterprise: 'success' };
const PLAN_LABELS = { basic: 'Básico', professional: 'Profissional', enterprise: 'Enterprise' };
const fmt = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

// ── Dashboard cards ──────────────────────────────────────────────────
function AdminDashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.dashboard().then(r => setData(r.data)).catch(() => toast.error('Erro ao carregar dashboard')).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!data) return null;

  return (
    <div>
      <PageHeader title="Painel Superadmin" subtitle="Visão geral de todas as empresas" />
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: '1.5rem' }}>
        <StatCard icon="🏢" label="Total de Empresas" value={data.total_tenants} color="#6366f1" bg="#ede9fe" />
        <StatCard icon="✅" label="Empresas Ativas" value={data.active_tenants} color="#22c55e" bg="#dcfce7" />
        <StatCard icon="💰" label="MRR Estimado" value={fmt(data.mrr)} color="#0ea5e9" bg="#e0f2fe" />
        <StatCard icon="⚠️" label="Vencendo em 7 dias" value={data.expiring_soon} color="#f59e0b" bg="#fef9c3" />
      </div>

      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Distribuição por Plano</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem' }}>
          {data.by_plan.map(p => (
            <div key={p.plan} style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: '1rem' }}>
              <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: '.5rem' }}>
                {PLAN_LABELS[p.plan] || p.plan}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>{p.count}</div>
              <div style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>{fmt(p.revenue)}/mês</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: '1rem', textAlign: 'right' }}>
        <button className="btn btn-primary" onClick={() => onNavigate('tenants')}>Ver todas as empresas →</button>
      </div>
    </div>
  );
}

// ── Tenant list ──────────────────────────────────────────────────────
function TenantList({ onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (planFilter) params.plan = planFilter;
      if (activeFilter !== '') params.is_active = activeFilter === 'true';
      const r = await adminApi.tenants(params);
      setItems(r.data);
    } catch { toast.error('Erro ao carregar empresas'); }
    finally { setLoading(false); }
  }, [search, planFilter, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id, name) => {
    if (!window.confirm(`Deseja ativar/desativar "${name}"?`)) return;
    try { await adminApi.toggleTenant(id); toast.success('Status atualizado'); load(); }
    catch { toast.error('Erro ao atualizar'); }
  };

  const columns = [
    { key: 'name', label: 'Empresa', render: t => (
      <div>
        <div style={{ fontWeight: 600 }}>{t.name}</div>
        <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{t.cnpj || t.email || '—'}</div>
      </div>
    )},
    { key: 'plan', label: 'Plano', render: t => (
      <div>
        <Badge status={PLAN_COLORS[t.plan] || 'secondary'}>{t.plan_name}</Badge>
        <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{fmt(t.plan_price)}/mês</div>
      </div>
    )},
    { key: 'expires', label: 'Vencimento', render: t => {
      if (!t.plan_expires_at) return <span style={{ color: 'var(--text-muted)' }}>Sem vencimento</span>;
      const color = t.days_left <= 0 ? '#ef4444' : t.days_left <= 7 ? '#f59e0b' : 'var(--text-muted)';
      return <span style={{ color }}>{fmtDate(t.plan_expires_at)}{t.days_left !== null ? ` (${t.days_left}d)` : ''}</span>;
    }},
    { key: 'usage', label: 'Uso', render: t => (
      <div style={{ fontSize: '.82rem' }}>
        👤 {t.users} &nbsp; 🚽 {t.assets} &nbsp; 📄 {t.contracts}
      </div>
    )},
    { key: 'status', label: 'Status', render: t => (
      <Badge status={t.is_active ? 'ativo' : 'inativo'}>{t.is_active ? 'Ativo' : 'Inativo'}</Badge>
    )},
    { key: 'created_at', label: 'Cadastro', render: t => fmtDate(t.created_at) },
    { key: '__actions', label: '', render: t => (
      <div className="table-actions">
        <button className="btn btn-sm btn-primary" onClick={() => onSelect(t.id)}>Gerenciar</button>
        <button className={`btn btn-sm ${t.is_active ? 'btn-danger' : 'btn-success'}`} onClick={() => toggle(t.id, t.name)}>
          {t.is_active ? 'Suspender' : 'Ativar'}
        </button>
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader title="Empresas" subtitle={`${items.length} cadastradas`} />
      <div className="filters-bar">
        <input className="input search-input" placeholder="Buscar por nome, CNPJ ou e-mail..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" value={planFilter} onChange={e => setPlanFilter(e.target.value)} style={{ width: 160 }}>
          <option value="">Todos os planos</option>
          <option value="basic">Básico</option>
          <option value="professional">Profissional</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select className="input" value={activeFilter} onChange={e => setActiveFilter(e.target.value)} style={{ width: 130 }}>
          <option value="">Todos status</option>
          <option value="true">Ativos</option>
          <option value="false">Suspensos</option>
        </select>
      </div>
      {!loading && items.length === 0
        ? <EmptyState title="Nenhuma empresa encontrada" />
        : <Table columns={columns} data={items} loading={loading} />
      }
    </div>
  );
}

// ── Tenant detail modal ──────────────────────────────────────────────
function TenantDetail({ tenantId, onClose, onRefresh, planPrices = { basic: 149, professional: 299, enterprise: 599 } }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [extendDays, setExtendDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try { const r = await adminApi.getTenant(tenantId); setData(r.data); setForm({ plan: r.data.plan, notes: r.data.notes || '', plan_expires_at: r.data.plan_expires_at?.slice(0,10) || '' }); }
    catch { toast.error('Erro ao carregar empresa'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (tenantId) load(); }, [tenantId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.plan_expires_at) payload.plan_expires_at = new Date(payload.plan_expires_at).toISOString();
      else delete payload.plan_expires_at;
      await adminApi.updateTenant(tenantId, payload);
      toast.success('Empresa atualizada');
      setEditing(false);
      load();
      onRefresh();
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const extend = async () => {
    try {
      const r = await adminApi.extendPlan(tenantId, extendDays);
      toast.success(`Plano estendido até ${fmtDate(r.data.plan_expires_at)}`);
      load();
    } catch { toast.error('Erro ao estender plano'); }
  };

  if (loading) return <Modal isOpen={true} onClose={onClose} title="Carregando..."><Loading /></Modal>;
  if (!data) return null;

  const planInfo = { basic: { price: 149 }, professional: { price: 299 }, enterprise: { price: 599 } };

  return (
    <Modal isOpen={true} onClose={onClose} title={data.name} size="lg">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="stat-card"><div className="stat-info"><div className="stat-value">{data.users}</div><div className="stat-label">Usuários</div></div></div>
        <div className="stat-card"><div className="stat-info"><div className="stat-value">{data.assets}</div><div className="stat-label">Ativos</div></div></div>
        <div className="stat-card"><div className="stat-info"><div className="stat-value">{data.contracts}</div><div className="stat-label">Contratos</div></div></div>
      </div>

      {/* Plan & billing */}
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, fontWeight: 600 }}>Plano & Cobrança</h4>
          <button className="btn btn-sm btn-secondary" onClick={() => setEditing(e => !e)}>
            {editing ? 'Cancelar' : 'Editar'}
          </button>
        </div>

        {editing ? (
          <div>
            <div className="form-grid-2">
              <FormGroup label="Plano">
                <Select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
                  <option value="basic">{`Básico — R$ ${planPrices.basic}/mês`}</option>
                  <option value="professional">{`Profissional — R$ ${planPrices.professional}/mês`}</option>
                  <option value="enterprise">{`Enterprise — R$ ${planPrices.enterprise}/mês`}</option>
                </Select>
              </FormGroup>
              <FormGroup label="Vencimento do plano">
                <Input type="date" value={form.plan_expires_at} onChange={e => setForm(f => ({ ...f, plan_expires_at: e.target.value }))} />
              </FormGroup>
            </div>
            <FormGroup label="Observações internas">
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </FormGroup>
            <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem', fontSize: '.9rem' }}>
            <div><span style={{ color: 'var(--text-muted)' }}>Plano:</span> <Badge status={PLAN_COLORS[data.plan] || 'secondary'}>{PLAN_LABELS[data.plan] || data.plan}</Badge></div>
            <div><span style={{ color: 'var(--text-muted)' }}>Valor:</span> {fmt(planInfo[data.plan]?.price || 0)}/mês</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Vencimento:</span> {fmtDate(data.plan_expires_at)}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Status:</span> <Badge status={data.is_active ? 'ativo' : 'inativo'}>{data.is_active ? 'Ativo' : 'Suspenso'}</Badge></div>
            {data.notes && <div style={{ gridColumn: '1/-1', color: 'var(--text-muted)', fontSize: '.85rem' }}>📝 {data.notes}</div>}
          </div>
        )}

        {/* Quick extend */}
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>Estender plano:</span>
          <select className="input" style={{ width: 100 }} value={extendDays} onChange={e => setExtendDays(Number(e.target.value))}>
            <option value={30}>30 dias</option>
            <option value={60}>60 dias</option>
            <option value={90}>90 dias</option>
            <option value={365}>1 ano</option>
          </select>
          <button className="btn btn-sm btn-success" onClick={extend}>Estender</button>
        </div>
      </div>

      {/* Users */}
      <div className="card" style={{ padding: '1.25rem' }}>
        <h4 style={{ margin: '0 0 .75rem', fontWeight: 600 }}>Usuários ({data.users})</h4>
        <table className="table" style={{ fontSize: '.85rem' }}>
          <thead><tr><th>Nome</th><th>E-mail</th><th>Perfil</th><th>Status</th></tr></thead>
          <tbody>
            {(Array.isArray(data.users) ? data.users : []).map(u => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td><Badge status={u.role}>{u.role}</Badge></td>
                <td><Badge status={u.is_active ? 'ativo' : 'inativo'}>{u.is_active ? 'Ativo' : 'Inativo'}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// ── Plans page ───────────────────────────────────────────────────────
function PlansPage() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadPlans = async () => {
    setPlans(null);  // force re-render
    const r = await api.get('/superadmin/plans');
    setPlans(r.data);
  };

  useEffect(() => { loadPlans(); }, []);

  const startEdit = (key, plan) => {
    setEditing(key);
    setForm({ ...plan, features: plan.features?.join('\n') || '' });
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/superadmin/plans/${editing}`, {
        ...form,
        price: parseFloat(form.price),
        max_users: parseInt(form.max_users),
        max_assets: parseInt(form.max_assets),
        features: form.features.split('\n').filter(Boolean),
      });
      toast.success('Plano atualizado!');
      setEditing(null);
      loadPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  if (!plans) return <Loading />;

  return (
    <div>
      <PageHeader title="Planos" subtitle="Edite os valores e limites de cada plano" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1.5rem' }}>
        {Object.entries(plans).map(([key, plan]) => (
          <div key={key} className="card" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.75rem' }}>
              <Badge status={PLAN_COLORS[key] || 'secondary'}>{plan.name}</Badge>
              <button className="btn btn-sm btn-secondary" onClick={() => startEdit(key, plan)}>✏️ Editar</button>
            </div>

            {editing === key ? (
              <div>
                <FormGroup label="Nome">
                  <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
                </FormGroup>
                <FormGroup label="Preço (R$/mês)">
                  <Input type="number" step="0.01" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} />
                </FormGroup>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem' }}>
                  <FormGroup label="Máx. usuários (-1=∞)">
                    <Input type="number" value={form.max_users} onChange={e => setForm(f => ({...f, max_users: e.target.value}))} />
                  </FormGroup>
                  <FormGroup label="Máx. ativos (-1=∞)">
                    <Input type="number" value={form.max_assets} onChange={e => setForm(f => ({...f, max_assets: e.target.value}))} />
                  </FormGroup>
                </div>
                <FormGroup label="Funcionalidades (uma por linha)">
                  <textarea className="input" rows={4} value={form.features}
                    onChange={e => setForm(f => ({...f, features: e.target.value}))}
                    style={{ resize: 'vertical' }} />
                </FormGroup>
                <div style={{ display: 'flex', gap: '.5rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={savePlan} disabled={saving} style={{ flex: 1 }}>
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)} style={{ flex: 1 }}>
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: '2rem', fontWeight: 700, margin: '.5rem 0 .25rem', color: 'var(--text-primary)' }}>
                  {fmt(plan.price)}<span style={{ fontSize: '1rem', fontWeight: 400, color: 'var(--text-muted)' }}>/mês</span>
                </div>
                <div style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  👤 {plan.max_users === -1 ? '∞' : `até ${plan.max_users}`} usuários &nbsp;·&nbsp; 🚽 {plan.max_assets === -1 ? '∞' : `até ${plan.max_assets}`} ativos
                </div>
                <ul style={{ paddingLeft: '1.2rem', margin: 0, fontSize: '.88rem', color: 'var(--text-secondary)' }}>
                  {(plan.features || []).map(f => <li key={f} style={{ marginBottom: '.3rem' }}>✓ {f}</li>)}
                </ul>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Billing Page ─────────────────────────────────────────────────────
function BillingPage({ planPrices = { basic: 149, professional: 299, enterprise: 599 } }) {
  const [tenants, setTenants] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [plan, setPlan] = useState('basic');
  const [months, setMonths] = useState(1);
  const [pixResult, setPixResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pixConfig, setPixConfig] = useState(null);

  useEffect(() => {
    Promise.all([
      adminApi.tenants().then(r => setTenants(r.data)),
      api.get('/billing/overview').then(r => setOverview(r.data)),
      api.get('/billing/config').then(r => setPixConfig(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const loadBilling = async (t) => {
    setSelected(t);
    setBillingLoading(true);
    setPixResult(null);
    try {
      const r = await api.get(`/billing/tenant/${t.id}`);
      setBilling(r.data);
      setPlan(r.data.plan || 'basic');
    } catch { setBilling(null); }
    finally { setBillingLoading(false); }
  };

  const generatePix = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/billing/generate-pix', { tenant_id: selected.id, plan, months });
      setPixResult(r.data);
      toast.success('PIX gerado! Aguarde o pagamento e confirme abaixo.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar PIX');
    } finally { setGenerating(false); }
  };

  const confirmPayment = async () => {
    if (!window.confirm(`Confirmar recebimento do PIX de ${selected.name}?`)) return;
    setConfirming(true);
    try {
      const r = await api.post('/billing/confirm-payment', {
        tenant_id: selected.id, plan,
        months, payment_ref: pixResult?.payment_ref,
      });
      toast.success(`✅ Pagamento confirmado! Plano ativo até ${new Date(r.data.plan_expires_at).toLocaleDateString('pt-BR')}`);
      setPixResult(null);
      loadBilling(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar');
    } finally { setConfirming(false); }
  };

  const confirmManual = async (t) => {
    const months = parseInt(window.prompt('Quantos meses confirmar?', '1') || '1');
    if (!months) return;
    try {
      const r = await api.post('/billing/confirm-payment', { tenant_id: t.id, plan: t.plan, months, payment_ref: 'manual' });
      toast.success(`✅ Pagamento manual confirmado!`);
      const r2 = await api.get('/billing/overview');
      setOverview(r2.data);
      if (selected?.id === t.id) loadBilling(t);
    } catch (err) { toast.error('Erro ao confirmar'); }
  };

  const fmt = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const fmtDt = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

  if (loading) return <Loading />;

  return (
    <div>
      {/* Config warning */}
      {pixConfig && !pixConfig.configured && (
        <div style={{ background: '#fef9c3', border: '1px solid #f59e0b', borderRadius: 8, padding: '1rem', marginBottom: '1.5rem', fontSize: '.875rem' }}>
          ⚠️ <strong>Chave PIX não configurada.</strong> Edite <code>/sanitech/.env</code> na VPS e adicione:<br/>
          <code>PIX_KEY=sua_chave_pix_c6</code> (CNPJ, e-mail, telefone ou chave aleatória)<br/>
          <code>PIX_MERCHANT_NAME=Seu Nome ou Empresa</code><br/>
          Depois: <code>docker compose restart backend</code>
        </div>
      )}

      {/* Overview cards */}
      {overview && (
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: '1.5rem' }}>
          <StatCard icon="🏢" label="Empresas" value={overview.summary.total} color="#6366f1" bg="#ede9fe" />
          <StatCard icon="✅" label="Ativas" value={overview.summary.active} color="#22c55e" bg="#dcfce7" />
          <StatCard icon="💰" label="MRR" value={fmt(overview.summary.mrr)} color="#0ea5e9" bg="#e0f2fe" />
          <StatCard icon="⚠️" label="Vence em 7d" value={overview.summary.expiring_7d} color="#f59e0b" bg="#fef9c3" />
          <StatCard icon="🔴" label="Vencidas" value={overview.summary.overdue} color="#ef4444" bg="#fee2e2" />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '1.5rem' }}>
        {/* Tenant list */}
        <div className="card" style={{ padding: '1.25rem', maxHeight: '75vh', overflowY: 'auto' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Empresas</h3>
          {tenants.map(t => {
            const days = overview?.tenants?.find(x => x.id === t.id)?.days_left;
            const hasPending = overview?.tenants?.find(x => x.id === t.id)?.has_pending;
            const color = days === null ? '#6b7280' : days < 0 ? '#ef4444' : days <= 7 ? '#f59e0b' : '#22c55e';
            return (
              <div key={t.id} onClick={() => loadBilling(t)}
                style={{ padding: '.75rem', borderRadius: 8, cursor: 'pointer', marginBottom: '.5rem',
                  background: selected?.id === t.id ? 'var(--primary-light)' : 'var(--bg-secondary)',
                  border: selected?.id === t.id ? '1.5px solid var(--primary)' : '1.5px solid transparent' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{t.name}</div>
                  {hasPending && <span style={{ fontSize: '.7rem', background: '#fef9c3', color: '#92400e', padding: '1px 6px', borderRadius: 10 }}>⏳ Pendente</span>}
                </div>
                <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                  <Badge status={PLAN_COLORS[t.plan] || 'secondary'}>{t.plan_name}</Badge>
                  {days !== null && <span style={{ color }}>{days < 0 ? `Venceu há ${Math.abs(days)}d` : `${days}d restantes`}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail */}
        <div>
          {!selected && (
            <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💳</div>
              <p>Selecione uma empresa para gerar cobrança</p>
            </div>
          )}

          {selected && billingLoading && <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Carregando...</div>}

          {selected && !billingLoading && billing && (
            <>
              {/* Status */}
              <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{selected.name}</h3>
                  <Badge status={billing.is_active ? 'ativo' : 'inativo'}>{billing.is_active ? 'Ativo' : 'Suspenso'}</Badge>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', fontSize: '.875rem', marginBottom: '1rem' }}>
                  <div><span style={{ color: 'var(--text-muted)' }}>Plano:</span><br/><Badge status={PLAN_COLORS[billing.plan] || 'secondary'}>{billing.plan_name}</Badge></div>
                  <div><span style={{ color: 'var(--text-muted)' }}>Mensalidade:</span><br/><strong>{fmt(billing.plan_price)}</strong></div>
                  <div><span style={{ color: 'var(--text-muted)' }}>Vencimento:</span><br/>
                    <strong style={{ color: billing.days_left < 0 ? '#ef4444' : billing.days_left <= 7 ? '#f59e0b' : undefined }}>
                      {fmtDt(billing.plan_expires_at)}{billing.days_left !== null ? ` (${billing.days_left}d)` : ''}
                    </strong>
                  </div>
                  <div><span style={{ color: 'var(--text-muted)' }}>Pend. PIX:</span><br/>
                    {billing.pending_payment ? <span style={{ color: '#f59e0b' }}>⏳ {fmt(billing.pending_payment.amount)}</span> : <span style={{ color: '#22c55e' }}>Nenhum</span>}
                  </div>
                </div>

                {/* Generate PIX */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                  <p style={{ fontWeight: 600, fontSize: '.875rem', marginBottom: '.75rem' }}>Gerar Cobrança PIX</p>
                  <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.75rem' }}>
                    <select className="input" value={plan} onChange={e => setPlan(e.target.value)} style={{ flex: 2 }}>
                    <option value="basic">{`Básico — R$ ${planPrices.basic}/mês`}</option>
                    <option value="professional">{`Profissional — R$ ${planPrices.professional}/mês`}</option>
                    <option value="enterprise">{`Enterprise — R$ ${planPrices.enterprise}/mês`}</option>
                    </select>
                    <select className="input" value={months} onChange={e => setMonths(Number(e.target.value))} style={{ flex: 1 }}>
                      {[1,2,3,6,12].map(m => <option key={m} value={m}>{m} {m===1?'mês':'meses'}</option>)}
                    </select>
                  </div>
                  <button className="btn btn-primary" onClick={generatePix} disabled={generating || !pixConfig?.configured}
                    style={{ width: '100%', justifyContent: 'center' }}>
                    {generating ? 'Gerando...' : `💳 Gerar PIX — ${fmt(PLAN_PRICES[plan]*months)}`}
                  </button>
                </div>
              </div>

              {/* PIX Result */}
              {pixResult && (
                <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem', textAlign: 'center' }}>
                  <p style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '.25rem' }}>
                    ✅ PIX Gerado — {fmt(pixResult.amount)}
                  </p>
                  <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                    {pixResult.plan_name} · {pixResult.months} {pixResult.months===1?'mês':'meses'} · Ref: {pixResult.payment_ref}
                  </p>
                  {pixResult.pix_qrcode && (
                    <img src={`data:image/png;base64,${pixResult.pix_qrcode}`} alt="QR Code PIX"
                      style={{ width: 180, height: 180, margin: '0 auto .75rem', display: 'block', borderRadius: 8 }} />
                  )}
                  <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '.75rem', marginBottom: '.75rem', wordBreak: 'break-all' }}>
                    <p style={{ fontSize: '.7rem', color: 'var(--text-muted)', margin: '0 0 .25rem' }}>Copia e Cola PIX:</p>
                    <code style={{ fontSize: '.7rem' }}>{pixResult.pix_payload}</code>
                  </div>
                  <div style={{ display: 'flex', gap: '.5rem' }}>
                    <button className="btn btn-secondary" style={{ flex: 1 }}
                      onClick={() => { navigator.clipboard.writeText(pixResult.pix_payload); toast.success('Copiado!'); }}>
                      📋 Copiar PIX
                    </button>
                    <button className="btn btn-success" style={{ flex: 1 }} onClick={confirmPayment} disabled={confirming}>
                      {confirming ? 'Confirmando...' : '✅ Confirmar Pagamento'}
                    </button>
                  </div>
                </div>
              )}

              {/* Payment history */}
              {billing.payment_history?.length > 0 && (
                <div className="card" style={{ padding: '1.25rem' }}>
                  <h4 style={{ margin: '0 0 .75rem', fontWeight: 600, fontSize: '.9rem' }}>Histórico de Pagamentos</h4>
                  {billing.payment_history.map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '.6rem .75rem', borderRadius: 8, background: 'var(--bg-secondary)', marginBottom: '.5rem', fontSize: '.82rem' }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{fmt(p.amount)}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>{p.plan_name || p.plan} · {p.months}m · {p.method}</div>
                      </div>
                      <div style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: '.75rem' }}>
                        {new Date(p.confirmed_at).toLocaleDateString('pt-BR')}<br/>
                        <code style={{ fontSize: '.68rem' }}>{p.ref}</code>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Define PLAN_PRICES for frontend use
const PLAN_PRICES = { basic: 149, professional: 299, enterprise: 599 };


// ── Backup Admin Page ─────────────────────────────────────────────────
function BackupAdminPage() {
  const [tenants, setTenants] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [dbDumping, setDbDumping] = useState(false);

  useEffect(() => {
    adminApi.tenants().then(r => setTenants(r.data));
  }, []);

  const downloadTenantBackup = async (tenant) => {
    setDownloading(tenant.id);
    try {
      // Need to impersonate — for now show instructions
      toast('Para baixar backup de um tenant específico, use a exportação no painel do tenant.', { icon: 'ℹ️' });
    } finally { setDownloading(null); }
  };

  const downloadDbDump = async () => {
    setDbDumping(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch('/api/backup/database/dump', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || 'Erro');
      }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `sanitech_db_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      toast.success('Dump do banco baixado!');
    } catch (err) {
      toast.error(err.message || 'Erro ao gerar dump');
    } finally { setDbDumping(false); }
  };

  return (
    <div>
      <PageHeader title="Backup" subtitle="Exportação e backup do banco de dados" />

      {/* DB Dump */}
      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem', fontWeight: 600 }}>🗄️ Dump Completo do Banco</h3>
        <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
          Exporta todo o banco PostgreSQL em formato SQL via pg_dump.
          Inclui dados de todos os tenants. Use para migração ou recuperação de desastre.
        </p>
        <button className="btn btn-primary" onClick={downloadDbDump} disabled={dbDumping}>
          {dbDumping ? '⏳ Gerando dump...' : '💾 Baixar Dump SQL (todos os tenants)'}
        </button>
      </div>

      {/* Cron instructions */}
      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>⏰ Backup Automático (Cron)</h3>
        <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Configure um cron job na VPS para backup automático diário:
        </p>
        <div style={{ background: '#1e293b', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
          <code style={{ color: '#a5f3fc', fontSize: '.8rem', whiteSpace: 'pre-wrap' }}>
{`# Edite o crontab: crontab -e

# Backup diário às 2h da manhã
0 2 * * * docker exec sanitech_db pg_dump \
  -U sanitech sanitech \
  | gzip > /backups/sanitech_$(date +%Y%m%d).sql.gz

# Manter apenas os últimos 7 dias
0 3 * * * find /backups -name "sanitech_*.sql.gz" -mtime +7 -delete

# Criar pasta de backups:
# mkdir -p /backups`}
          </code>
        </div>
        <div style={{ background: '#fef9c3', borderRadius: 8, padding: '.75rem', fontSize: '.8rem', color: '#92400e' }}>
          💡 <strong>Dica:</strong> Configure também o upload automático para Google Drive ou S3 para backup offsite.
        </div>
      </div>

      {/* Tenant list */}
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>📦 Exportação por Empresa</h3>
        <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Cada empresa pode exportar seus dados em JSON pelo menu Configurações do próprio painel.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '.75rem' }}>
          {tenants.filter(t => t.slug !== '__system__').map(t => (
            <div key={t.id} style={{ padding: '.75rem', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <div style={{ fontWeight: 600, fontSize: '.875rem', marginBottom: '.25rem' }}>{t.name}</div>
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
                <Badge status={PLAN_COLORS[t.plan] || 'secondary'}>{t.plan_name}</Badge>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main SuperAdmin Portal ───────────────────────────────────────────
export default function SuperAdminPortal() {
  const { user } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [planPrices, setPlanPrices] = useState({ basic: 149, professional: 299, enterprise: 599 });
  React.useEffect(() => {
    api.get('/superadmin/plans').then(r => {
      const p = r.data;
      setPlanPrices({ basic: p.basic?.price||149, professional: p.professional?.price||299, enterprise: p.enterprise?.price||599 });
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    api.get('/superadmin/plans').then(r => {
      const p = r.data;
      setPlanPrices({ basic: p.basic?.price || 149, professional: p.professional?.price || 299, enterprise: p.enterprise?.price || 599 });
    }).catch(() => {});
  }, [page]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  if (user?.role !== 'superadmin') {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2>🔒 Acesso Restrito</h2>
        <p>Esta área é exclusiva para superadministradores.</p>
      </div>
    );
  }

  const navItems = [
    { key: 'dashboard', label: '📊 Dashboard' },
    { key: 'tenants', label: '🏢 Empresas' },
    { key: 'plans', label: '💼 Planos' },
    { key: 'billing', label: '💳 Cobranças' },
    { key: 'backup', label: '💾 Backup' },
  ];

  return (
    <div className="page">
      {/* Superadmin top bar */}
      <div style={{ background: '#1e1b4b', color: '#fff', padding: '.75rem 2rem', display: 'flex', alignItems: 'center', gap: '2rem', marginBottom: '1.5rem', borderRadius: 10 }}>
        <span style={{ fontWeight: 700, fontSize: '1rem' }}>🛡️ Superadmin</span>
        {navItems.map(n => (
          <button key={n.key} onClick={() => setPage(n.key)}
            style={{ background: page === n.key ? 'rgba(255,255,255,.15)' : 'transparent', border: 'none', color: '#fff', padding: '.4rem .9rem', borderRadius: 6, cursor: 'pointer', fontSize: '.88rem' }}>
            {n.label}
          </button>
        ))}
      </div>

      {page === 'dashboard' && <AdminDashboard onNavigate={setPage} />}
      {page === 'tenants' && (
        <TenantList
          key={refreshKey}
          onSelect={id => setSelectedTenant(id)}
        />
      )}
      {page === 'plans' && <PlansPage />}
      {page === 'billing' && <BillingPage planPrices={planPrices} />}
      {page === 'backup' && <BackupAdminPage />}

      {selectedTenant && (
        <TenantDetail
          planPrices={planPrices}
          tenantId={selectedTenant}
          onClose={() => setSelectedTenant(null)}
          onRefresh={() => setRefreshKey(k => k + 1)}
        />
      )}
    </div>
  );
}
